import { StepEditorProps } from "../useStepEditor";
import { StepEditorAccordion } from "../../../workflows/routes/WorkflowEditor/StepEditorAccordion";
import { Block } from "../../../../components/atoms";
import { useStepOptions } from "../useStepOptions";
import { prefixForm } from "../../../../utils/FormUtils";
import { useDataSources } from "../../../datasources/hooks/useDataSources";
import { useEffect, useMemo } from "react";
import { Field, Option, Select, Stack, Title } from "@introist/react-foundation/v2";
import { FieldAwareInput } from "../../../employees/fields/FieldAwareInput";
import styled from "styled-components";

const RecipientCard = styled.div`
  background: var(--palette-surface-subdued);
  border-radius: var(--rounding-medium);
  padding: var(--spacing-medium);
`;

const FieldMappingCard = styled.div`
  background: var(--palette-surface-subdued);
  border-radius: var(--rounding-medium);
  padding: var(--spacing-medium);
`;

type Recipient = {
  roleName: string;
  recipientId: string;
  email: string;
  name: string;
};

type FieldMapping = {
  tabLabel: string;
  tabId: string;
  value: string;
};

export const DocuSignStepEditor = ({ form, stepDataPath, workflowId }: StepEditorProps) => {
  const prefixedForm = prefixForm(form, stepDataPath);

  const { dataSources } = useDataSources();

  const { options } = useStepOptions(form.data.stepType, {
    datasourceId: prefixedForm.get("datasourceId"),
    templateId: prefixedForm.get("templateId")
  });

  const docusignSourceOptions = useMemo(() => {
    if (!dataSources) return [];
    return dataSources
      .filter(ds => ds.sourceType === "docusign")
      .map(s => ({ key: s.id, title: s.name }));
  }, [dataSources]);

  const templateOptions: Option[] = useMemo(() => {
    if (!options || !options.templates) return [];
    return options.templates.map((t: { templateId: string; name: string }) => ({
      key: t.templateId,
      title: t.name
    }));
  }, [options]);

  const recipients: Recipient[] = prefixedForm.get("recipients") || [];
  const fieldMappings: FieldMapping[] = prefixedForm.get("fieldMappings") || [];

  // Initialize recipients from template when template is selected
  useEffect(() => {
    if (options?.recipients && options.recipients.length > 0) {
      const currentRecipients = prefixedForm.get("recipients") || [];

      // Check if we need to sync recipients from template
      const templateRoleNames = options.recipients.map(
        (r: { roleName: string }) => r.roleName
      );
      const currentRoleNames = currentRecipients.map((r: Recipient) => r.roleName);

      const needsSync =
        templateRoleNames.length !== currentRoleNames.length ||
        !templateRoleNames.every((role: string) => currentRoleNames.includes(role));

      if (needsSync) {
        const newRecipients = options.recipients.map(
          (r: { recipientId: string; roleName: string }) => {
            const existing = currentRecipients.find(
              (cr: Recipient) => cr.roleName === r.roleName
            );
            return {
              roleName: r.roleName,
              recipientId: r.recipientId,
              email: existing?.email || "",
              name: existing?.name || ""
            };
          }
        );
        prefixedForm.set("recipients")(newRecipients);
      }
    }
  }, [options?.recipients, prefixedForm]);

  // Initialize field mappings from template when template is selected
  useEffect(() => {
    if (options?.prefillTabs && options.prefillTabs.length > 0) {
      const currentMappings = prefixedForm.get("fieldMappings") || [];

      const templateTabLabels = options.prefillTabs.map(
        (t: { tabLabel: string }) => t.tabLabel
      );
      const currentTabLabels = currentMappings.map((m: FieldMapping) => m.tabLabel);

      const needsSync =
        templateTabLabels.length !== currentTabLabels.length ||
        !templateTabLabels.every((label: string) => currentTabLabels.includes(label));

      if (needsSync) {
        const newMappings = options.prefillTabs.map(
          (t: { tabLabel: string; tabId: string }) => {
            const existing = currentMappings.find(
              (cm: FieldMapping) => cm.tabLabel === t.tabLabel
            );
            return {
              tabLabel: t.tabLabel,
              tabId: t.tabId,
              value: existing?.value || ""
            };
          }
        );
        prefixedForm.set("fieldMappings")(newMappings);
      }
    }
  }, [options?.prefillTabs, prefixedForm]);

  const updateRecipient = (index: number, field: keyof Recipient, value: string) => {
    const updated = [...recipients];
    updated[index] = { ...updated[index], [field]: value };
    prefixedForm.set("recipients")(updated);
  };

  const updateFieldMapping = (index: number, value: string) => {
    const updated = [...fieldMappings];
    updated[index] = { ...updated[index], value };
    prefixedForm.set("fieldMappings")(updated);
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = options?.templates?.find(
      (t: { templateId: string; name: string }) => t.templateId === templateId
    );
    prefixedForm.setMany({
      templateId,
      templateName: template?.name || "",
      recipients: [],
      fieldMappings: []
    });
  };

  return (
    <>
      <Block blended compact>
        <StepEditorAccordion
          title="Template"
          defaultOpen={!form.data.id}
          description={
            templateOptions.find(o => o.key === prefixedForm.get("templateId"))?.title
          }
        >
          <StepEditorAccordion.Row label="Integration">
            <Select
              size="small"
              element="button"
              variant="blended"
              style={{ width: "100%" }}
              options={docusignSourceOptions}
              value={prefixedForm.get("datasourceId")}
              onSelect={opt => {
                prefixedForm.setMany({
                  datasourceId: opt.key,
                  templateId: "",
                  templateName: "",
                  recipients: [],
                  fieldMappings: []
                });
              }}
              placeholder="Select integration"
              attachToRef={false}
            />
          </StepEditorAccordion.Row>
          <StepEditorAccordion.Row label="Template">
            <Select
              size="small"
              element="button"
              variant="blended"
              style={{ width: "100%" }}
              options={templateOptions}
              value={prefixedForm.get("templateId")}
              onSelect={opt => handleTemplateSelect(opt.key)}
              placeholder="Select template"
              attachToRef={false}
            />
          </StepEditorAccordion.Row>
          <Field title="Email Subject (optional)">
            <FieldAwareInput
              value={prefixedForm.get("emailSubject") || ""}
              onChange={prefixedForm.set("emailSubject")}
              placeholder="Custom email subject"
              size="small"
              workflowId={workflowId}
            />
          </Field>
        </StepEditorAccordion>
      </Block>

      {recipients.length > 0 && (
        <Block blended compact>
          <StepEditorAccordion title="Recipients" defaultOpen>
            <Stack vertical gap="medium">
              {recipients.map((recipient, index) => (
                <RecipientCard key={recipient.recipientId}>
                  <Stack vertical gap="small">
                    <Title variant="bold">{recipient.roleName}</Title>
                    <FieldAwareInput
                      value={recipient.email}
                      onChange={value => updateRecipient(index, "email", value)}
                      placeholder="Email address"
                      size="small"
                      workflowId={workflowId}
                    />
                    <FieldAwareInput
                      value={recipient.name}
                      onChange={value => updateRecipient(index, "name", value)}
                      placeholder="Recipient name"
                      size="small"
                      workflowId={workflowId}
                    />
                  </Stack>
                </RecipientCard>
              ))}
            </Stack>
          </StepEditorAccordion>
        </Block>
      )}

      {fieldMappings.length > 0 && (
        <Block blended compact>
          <StepEditorAccordion title="Prefill Fields" defaultOpen>
            <Stack vertical gap="medium">
              {fieldMappings.map((mapping, index) => (
                <FieldMappingCard key={mapping.tabId}>
                  <Stack vertical gap="small">
                    <Title variant="bold">{mapping.tabLabel}</Title>
                    <FieldAwareInput
                      value={mapping.value}
                      onChange={value => updateFieldMapping(index, value)}
                      placeholder="Field value"
                      size="small"
                      workflowId={workflowId}
                    />
                  </Stack>
                </FieldMappingCard>
              ))}
            </Stack>
          </StepEditorAccordion>
        </Block>
      )}
    </>
  );
};
